import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { AlertService } from '../../../../../services/alert.service';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dropzone } from '@dropzone-ui/react';
import { AssetsServices } from '../../../../../services/assets.service';
import { optionsComplements } from '../../constants/data.constants';
import { PROFILES } from '../../../../../constants';
import { useSession } from '../../../../../hooks/use-session.hook';
import { SegmentedMessage } from 'sms-segments-calculator';
import { useLocation } from 'react-router-dom';
import Input from '../../../../../components/forms/input.component';
import Select from 'react-select';
import InvalidFeedback from '../../../../../components/forms/invalid-feedback.component';
import Icon from '../../../../../components/forms/icon.component';
import Title from '../../../../../components/forms/titles.component';
import Switch from '../../../../../components/forms/switch.component';
import TextArea from '../../../../../components/forms/text-area.component';
import EventsSection, { getChannelEventsConfig } from './events.component';

declare const KTComponents: any;
declare const KTThemeMode: any;
declare const KTSticky: any;

function ChannelSms({ onValidationChange }: { onValidationChange?: (isValid: boolean) => void }) {
  const { t } = useTranslation();
  const { session } = useSession();
  const location = useLocation();
  const showEvents = location.pathname.includes('sendCampaigns/flows-campaigns');
  const filteredMsgRef = useRef('');
  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  const [filesURL, setFilesURL] = useState<any[]>([]);
  const [optionsMasks, setOptionsMasks] = useState<{ value: string; label: string }[]>([]);
  const [emptyMaskSMS, setEmptyMaskSMS] = useState(false);
  const [eventsValid, setEventsValid] = useState(true);
  const [smsInfo, setSmsInfo] = useState({
    encodingName: '',
    segmentsCount: 0,
    numberOfCharacters: 0,
    messageProcessed: ''
  });

  const {
    register,
    control,
    watch,
    getValues,
    setValue,
    formState: { errors }
  } = useFormContext();

  const formValues = useMemo(() => {
    const values = watch();
    return {
      complements: values.complements,
      complementsSwitch: values.complementsSwitch,
      msgSMS: values.msgSMS,
      account: values.account,
      channel: values.channel,
      accents: values.accents,
      headersSelects: values.headersSelects,
      channelfallback: values.channelfallback,
      secondChannelfallback: values.secondChannelfallback,
      typeCampaign: values.typeCampaign,
      accountCahnnels: values.accountCahnnels,
      masks: values.masks,
      phone_number: values.phone_number,
      msgWhatsAppLink: values.msgWhatsAppLink,
      urlLink: values.urlLink,
      channelFlows: values.channelFlows,
      events: values.events
    };
  }, [watch()]);

  const gsm7Chars = useMemo(
    () =>
      ' @£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#¤%{}&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà',
    []
  );

  const variablePattern = useMemo(() => /\{\{\s*[^{}\s]+\s*\}\}/, []);
  const hasVariable = useMemo(
    () => variablePattern.test(formValues.msgSMS || ''),
    [formValues.msgSMS, variablePattern]
  );

  const maskSMSMessage = useMemo(() => {
    if (!emptyMaskSMS) return '';
    return session.profile === PROFILES.ADMIN
      ? 'La cuenta con la que estás trabajando no tiene máscaras registradas.'
      : 'La cuenta seleccionada en el paso 1 no tiene máscaras registradas.';
  }, [emptyMaskSMS, session.profile]);

  const fallbackChannelOptions = useMemo(() => {
    if (!formValues.channelFlows || !Array.isArray(formValues.channelFlows)) return [];

    const channelLabels: Record<string, string> = {
      email: 'Email'
    };
    return formValues.channelFlows
      .filter((channel) => channel === 'email')
      .map((channel) => ({
        value: channel,
        label: channelLabels[channel] || channel.toUpperCase()
      }));
  }, [formValues.channelFlows]);

  const eventsConfig = useMemo(() => {
    return getChannelEventsConfig('sms', fallbackChannelOptions, []);
  }, [fallbackChannelOptions]);

  const URLFile = useCallback(
    (filesURL: any[]) => {
      if (!filesURL?.length) return;
      const fileSizeMB = filesURL[0].size / (1024 * 1024);
      if (fileSizeMB > 5) {
        AlertService.toastInfo('¡ Atención !, El archivo es demasiado grande. Máximo: 5Mb.');
        return;
      }
      const data = new FormData();
      data.append('file', filesURL[0].file);

      const promise = async () => {
        const response = await AssetsServices.uploadMedia(data);
        setFilesURL([]);
        setValue(`urlLink`, response?.url || '', {
          shouldValidate: true
        });
      };

      AlertService.toastLoading(
        promise(),
        'Generando URL...',
        'La URL se generó correctamente',
        'Ocurrió un error al generar la URL, intenta nuevamente'
      );
    },
    [setValue]
  );

  const handleInsertVariable = useCallback(
    (variable: string) => {
      const currentValue = getValues('msgSMS');
      const formattedVariable = `{{${variable}}}`;
      const newValue = currentValue ? `${currentValue} ${formattedVariable}` : formattedVariable;

      setValue('msgSMS', newValue, { shouldValidate: true });
    },
    [getValues, setValue]
  );

  const handleComplementsToggle = useCallback(
    (checked: boolean) => {
      setValue('complementsSwitch', checked);
      if (!checked) {
        setValue('complements', '');
        setValue('msgWhatsAppLink', '');
        setValue('phone_number', '');
        setValue('urlLink', '');
      }
    },
    [setValue]
  );

  const handleComplementChange = useCallback(
    (selectedValue: string | null) => {
      if (selectedValue === 'LINK') {
        setValue('msgWhatsAppLink', '');
        setValue('phone_number', '');
      } else if (selectedValue === 'WHATSAPP_LINK') {
        setValue('urlLink', '');
      }
    },
    [setValue]
  );

  useEffect(() => {
    if (formValues.accountCahnnels?.sms) {
      const filtered = formValues.accountCahnnels.sms.filter(
        (item: any) => item.status === true && item.config?.status === true
      );

      if (filtered.length === 0) {
        setOptionsMasks([]);
        setEmptyMaskSMS(true);
        return;
      }

      const options = filtered.map(({ config }: any) => ({
        value: config._id,
        label: config.name
      }));

      setEmptyMaskSMS(false);
      setOptionsMasks(options);
    } else {
      setOptionsMasks([]);
      setEmptyMaskSMS(true);
    }
  }, [
    formValues.account,
    formValues.channel,
    formValues.channelfallback,
    formValues.secondChannelfallback,
    formValues.accountCahnnels
  ]);

  useEffect(() => {
    const shouldClearFields =
      formValues.typeCampaign !== 'FALLBACK' ||
      (formValues.typeCampaign === 'FALLBACK' && formValues.channel !== 'sms');

    if (shouldClearFields) {
      setValue('masks', '');
      setValue('complementsSwitch', false);
      setValue('complements', '');
      setValue('phone_number', '');
      setValue('msgWhatsAppLink', '');
      setValue('urlLink', '');
      setValue('accents', false);
      setValue('msgSMS', '');
      setValue('total_character', '');
      setValue('total_credits', '');
    }
  }, [
    formValues.channel,
    formValues.channelfallback,
    formValues.secondChannelfallback,
    formValues.typeCampaign,
    setValue
  ]);

  useEffect(() => {
    setValue('total_character', smsInfo.numberOfCharacters);
    setValue('total_credits', smsInfo.segmentsCount);
  }, [smsInfo, setValue]);

  useEffect(() => {
    if (formValues.channel === 'sms') {
      setValue('masks', '');
    }
  }, [formValues.account, formValues.channel, setValue]);

  useEffect(() => {
    if (!formValues.msgSMS) {
      setSmsInfo({
        encodingName: '',
        segmentsCount: 0,
        numberOfCharacters: 0,
        messageProcessed: ''
      });
      filteredMsgRef.current = '';
      return;
    }

    const hasComplement = formValues.complementsSwitch && formValues.complements;
    const complementLength = hasComplement ? 18 : 0;

    if (!formValues.accents) {
      const filteredText = formValues.msgSMS
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ç/g, 'c')
        .replace(/Ç/g, 'C')
        .split('')
        .filter((c: any) => gsm7Chars.includes(c))
        .join('');

      const segmented = new SegmentedMessage(filteredText + ' '.repeat(complementLength));

      setSmsInfo({
        encodingName: segmented.encodingName,
        segmentsCount: segmented.segmentsCount,
        numberOfCharacters: segmented.numberOfCharacters,
        messageProcessed: filteredText
      });

      if (filteredMsgRef.current !== filteredText) {
        filteredMsgRef.current = filteredText;
        setValue('msgSMS', filteredText, { shouldValidate: true });
      }
    } else {
      const totalLength = [...formValues.msgSMS].length + complementLength;

      let segmentsCount;
      if (totalLength <= 70) {
        segmentsCount = 1;
      } else {
        segmentsCount = Math.ceil(totalLength / 67);
      }

      setSmsInfo({
        encodingName: 'UCS-2',
        segmentsCount,
        numberOfCharacters: totalLength,
        messageProcessed: formValues.msgSMS
      });

      if (filteredMsgRef.current !== formValues.msgSMS) {
        filteredMsgRef.current = formValues.msgSMS;
        setValue('msgSMS', formValues.msgSMS, { shouldValidate: true });
      }
    }
  }, [
    formValues.msgSMS,
    formValues.accents,
    formValues.complements,
    formValues.complementsSwitch,
    gsm7Chars,
    setValue
  ]);

  useEffect(() => {
    let baseValidation = !!formValues.masks && !!formValues.msgSMS;

    if (formValues.complementsSwitch) {
      const isComplementSelected = !!formValues.complements;
      if (!isComplementSelected) {
        baseValidation = false;
      } else {
        const complementValidators: Record<string, boolean> = {
          WHATSAPP_LINK: !!formValues.phone_number && !!formValues.msgWhatsAppLink,
          LINK: !!formValues.urlLink
        };
        const isComplementValid = complementValidators[formValues.complements] ?? false;
        baseValidation = baseValidation && isComplementValid;
      }
    }

    const isValid = baseValidation && (showEvents ? eventsValid : true);

    if (onValidationChangeRef.current) {
      onValidationChangeRef.current(isValid);
    }
  }, [
    formValues.masks,
    formValues.msgSMS,
    formValues.complementsSwitch,
    formValues.complements,
    formValues.phone_number,
    formValues.msgWhatsAppLink,
    formValues.urlLink,
    eventsValid
  ]);

  useEffect(() => {
    KTComponents.init();
    KTThemeMode.init();
    KTSticky.init();
  }, [formValues.channel, formValues.accents]);

  return (
    <>
      <div className="separator separator-dashed separator-content text-dark mb-15 border-2 my-10">
        <img alt="Logo" src="/assets/media/logos/logo-c.svg" className="w-30px logo-minimize" />
      </div>
      <div className="mb-5 mb-xxl-8">
        <div className="text-left mb-3">
          <Title icon="ki-message-text-2" title={'Configuración SMS'} subtitle={''} type="dark" />
        </div>
        <div className="pt-1">
          <div className="d-flex flex-stack cursor-pointer mb-5">
            <div className="d-flex align-items-center">
              <span className="symbol symbol-label"></span>
              <div className="d-flex flex-column">
                <span className="fw-bolder fs-3 required">Máscara</span>
                <span className="fs-6">Elige la máscara para enviar tu mensaje.</span>
              </div>
            </div>
          </div>
          <div className="row mb-10">
            <div className="col-md-12 z-index-3">
              <div className="fv-row  mb-5 z-index-3">
                <Controller
                  control={control}
                  name="masks"
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <Select
                        options={optionsMasks}
                        noOptionsMessage={({ inputValue }) =>
                          inputValue
                            ? t('general.no-options', { inputValue })
                            : t('general.no-options')
                        }
                        className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                        classNamePrefix="form-select-custom"
                        placeholder={'Selecciona máscara'}
                        onChange={(
                          selectedOption: {
                            value: string;
                            label: string;
                          } | null
                        ) => {
                          onChange(selectedOption ? selectedOption.value : null);
                        }}
                        value={
                          optionsMasks.find(
                            (option: { value: string; label: string }) => option.value === value
                          ) || null
                        }
                      />
                      <InvalidFeedback error={error} />
                    </>
                  )}
                />
              </div>
              {emptyMaskSMS && (
                <div className="alert alert-dismissible bg-light-info border border-info d-flex border-dashed flex-column flex-sm-row p-5 mb-10 animate-fadeInUp animate-delay-1">
                  <Icon icon="ki-information-2" size="fs-2hx" type="info" />
                  <div className="d-flex flex-column pe-0 pe-sm-10 ms-5">
                    <h5 className="mb-1">¡ Atención !</h5>
                    <span>{maskSMSMessage}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="fv-row mb-10">
            <div className="d-flex flex-stack cursor-pointer mb-3">
              <div className="d-flex align-items-center">
                <span className="symbol me-e6 symbol-label"></span>
                <div className="d-flex flex-column">
                  <span className="fw-bolder fs-3 required">Mensaje</span>
                  <span className="fs-6">Personaliza tu mensaje aquí.</span>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex flex-stack cursor-pointer mb-10 ms-2">
            <div className="d-flex align-items-center me-2">
              <span className="symbol me-6 symbol-label">
                <Icon icon="ki-text-underline" size="fs-3x" />
              </span>
              <div className="d-flex flex-column">
                <span className="fw-bolder fs-3">Acentos y caracteres</span>
                <span className="fs-6">
                  Habilita esta opción para permitir el uso de acentos y caracteres especiales{' '}
                  <br />
                  en el mensaje.
                </span>
              </div>
            </div>
            <div className="form-check form-check-custom form-check-solid">
              <Switch {...register('accents')} />
            </div>
          </div>
          <div className="fv-row mb-7">
            <div className="mb-1">
              <div className="d-flex justify-content-start mb-5">
                <button
                  type="button"
                  className={`btn btn-light-primary btn-center`}
                  data-kt-menu-trigger="{default: 'hover', lg: 'hover'}"
                  data-kt-menu-attach="parent"
                  data-kt-menu-placement="bottom-start"
                >
                  <span className="fw-bolder">Variables {'{{...}}'}</span>
                </button>
                <div
                  className="menu menu-sub menu-sub-dropdown menu-column fw-semibold w-auto min-w-200px max-w-full text-nowrap w-350px bg-dropFilter"
                  data-kt-menu="true"
                >
                  <div className="bg-dropFilter">
                    <div className="card-header">
                      <div className="fs-5 text-dark fw-bolder mt-3">
                        Variables <br />
                        <span className="text-primary fs-7">
                          Da clic para insertar tu variable al mensaje.
                        </span>
                      </div>
                    </div>
                    <div className="card-body">
                      {formValues.headersSelects?.map((option: any) => (
                        <div className="menu-item" key={option.value}>
                          <button
                            className="btn menu-link btn-clean btn-sm btn-active-light-primary w-300px"
                            type="button"
                            onClick={() => handleInsertVariable(option.value)}
                          >
                            <span className="me-3">{`{{${option.value}}}`}</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <TextArea
                requiredIndicator="required"
                placeholder="Ingresa mensaje"
                errors={errors.msgSMS}
                onKeyDown={(e: any) => {
                  const specialKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                  if (
                    !formValues.accents &&
                    !gsm7Chars.includes(e.key) &&
                    !specialKeys.includes(e.key)
                  ) {
                    e.preventDefault();
                  }
                }}
                {...register('msgSMS', {
                  required: true
                })}
              />
            </div>
          </div>
          <div className="alert alert-dismissible bg-light-info border border-info border-dashed d-flex flex-column flex-sm-row p-5 mb-15">
            <Icon icon="ki-dollar" size="fs-4x" type="info" className="me-4 mb-5 mb-sm-0" />
            <div className="d-flex flex-column pe-0 pe-sm-10">
              <h5 className="mb-1">
                Caracteres utilizados:{' '}
                <span className="text-info fw-bolder">{smsInfo.numberOfCharacters}</span>
              </h5>
              <h5 className="mb-3">
                Costo del mensaje:{' '}
                <span className="text-info fw-bolder">{smsInfo.segmentsCount} créditos </span>
              </h5>
              {hasVariable && (
                <span className="text-danger fw-bolder">
                  El mensaje ingresado incluye variables. Los caracteres y/o el costo por cada
                  mensaje pueden variar según el valor real de cada registro de tu archivo cargado
                  previamente.
                </span>
              )}
            </div>
          </div>
          <div className="separator separator-dashed separator-content text-dark mb-15 border-2 my-10">
            <img alt="Logo" src="/assets/media/logos/logo-c.svg" className="w-30px logo-minimize" />
          </div>
          <div className="fv-row mb-5">
            <div className="d-flex flex-stack cursor-pointer mb-3">
              <div className="d-flex align-items-center">
                <span className="symbol me-e6 symbol-label"></span>
                <div className="d-flex flex-column">
                  <span className="fw-bolder fs-3">
                    Complementos <span className="fs-7">({t('general.optional')})</span>
                  </span>
                  <span className="fs-6">
                    Elige una de las opciones extra disponibles para complementar tu mensaje.
                  </span>
                </div>
              </div>
              <span className="form-check form-check-custom form-check-solid">
                <Switch
                  {...register('complementsSwitch')}
                  onChange={(e: any) => handleComplementsToggle(e.target.checked)}
                />
              </span>
            </div>
          </div>
          {formValues.complementsSwitch && (
            <div className="row mb-3">
              <div className="col-md-12 z-index-3">
                <div className="fv-row  mb-5 z-index-3">
                  <Controller
                    control={control}
                    name="complements"
                    rules={{ required: formValues.complementsSwitch }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <>
                        <Select
                          options={optionsComplements}
                          noOptionsMessage={({ inputValue }) =>
                            inputValue
                              ? t('general.no-options', { inputValue })
                              : t('general.no-options')
                          }
                          className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                          classNamePrefix="form-select-custom"
                          placeholder={'Selecciona un complemento'}
                          onChange={(
                            selectedOption: {
                              value: string;
                              label: string;
                            } | null
                          ) => {
                            const selectedValue = selectedOption ? selectedOption.value : null;
                            onChange(selectedValue);
                            handleComplementChange(selectedValue);
                          }}
                          value={
                            optionsComplements.find(
                              (option: { value: string; label: string }) => option.value === value
                            ) || null
                          }
                        />
                        <InvalidFeedback error={error} />
                      </>
                    )}
                  />
                </div>
              </div>
            </div>
          )}
          {formValues.complements === 'WHATSAPP_LINK' && (
            <div className="row">
              <div className="fv-row mb-10 col-md-6">
                <Input
                  label="Número"
                  requiredIndicator="required"
                  placeholder="Ingresa el número de 10 dígitos"
                  errors={errors?.phone_number}
                  {...register('phone_number', {
                    required: true,
                    pattern: {
                      value: /^\d{10}$/,
                      message: 'Ingresa un número válido, ejemplo: 5512345678'
                    },
                    setValueAs: (value) => value.trim()
                  })}
                />
              </div>
              <div className="fv-row mb-10 col-md-6">
                <Input
                  label="Mensaje WhatsApp"
                  requiredIndicator="required"
                  placeholder="Ingresa el mensaje de WhatsApp"
                  errors={errors?.msgWhatsAppLink}
                  {...register('msgWhatsAppLink', {
                    required: true,
                    setValueAs: (value) => value.trim()
                  })}
                />
              </div>
            </div>
          )}
          {formValues.complements === 'LINK' && (
            <>
              <div className="col-md-12 fv-row mb-7 animate-fadeInUp animate-delay-1">
                <Dropzone
                  onChange={URLFile}
                  value={filesURL}
                  maxFiles={1}
                  className="dropzone border border-dashed border-primary rounded-4"
                >
                  <div className="dz-message needsclick d-flex justify-content-between align-items-center w-100 p-3">
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <Icon icon="ki-file-up" size="fs-3x" type="primary" />
                      </div>
                      <div>
                        <h3 className="fs-6 fw-bold text-gray-900 mb-1">
                          Carga un archivo para generar una URL
                        </h3>
                        <span className="fs-7 fw-semibold text-info d-block">
                          Archivo/Audio/Video/Imagen
                        </span>
                      </div>
                    </div>
                  </div>
                </Dropzone>
              </div>
              <div className="fv-row mb-15">
                <Input
                  label="URL"
                  requiredIndicator="required"
                  placeholder="Ingresa el link"
                  errors={errors?.urlLink}
                  {...register('urlLink', {
                    required: true,
                    pattern: {
                      value: /^(https?:\/\/)([a-zA-Z0-9.-]+)(:[0-9]+)?(\/[^\s]*)?$/,
                      message:
                        'Ingresa una URL válida, ejemplo: https://www.dominio.com/nombre-archivo.pdf'
                    },
                    setValueAs: (value) => value.trim()
                  })}
                />
              </div>
            </>
          )}
        </div>

        {showEvents ? (
          <EventsSection
            channel="sms"
            events={eventsConfig}
            fallbackChannelOptions={fallbackChannelOptions}
            templateOptions={[]}
            onValidationChange={setEventsValid}
          />
        ) : null}
      </div>
    </>
  );
}

export default ChannelSms;
